import React, { useState, useEffect } from 'react';
import { generateCreditCard, calculateChecksum, getRandomPopularBin } from '../utils/helpers';
import { validateBinInput } from '../utils/validators';

const GeneratorForm = ({ isDarkMode, currentMode, showToast, showLoading, onGenerateComplete }) => {
  const [bin, setBin] = useState('');
  const [expMonth, setExpMonth] = useState('');
  const [expYear, setExpYear] = useState('');
  const [quantity, setQuantity] = useState(10);
  const [includeLuhn, setIncludeLuhn] = useState(true);
  const [binError, setBinError] = useState(false);

  useEffect(() => {
    const currentYear = new Date().getFullYear();
    const currentMonth = new Date().getMonth() + 1;
    setExpMonth(currentMonth.toString().padStart(2, '0'));
    setExpYear(currentYear.toString());
  }, []);

  useEffect(() => {
    if (currentMode === 'ordered') {
      setQuantity(1000);
    } else {
      setQuantity(10);
    }
  }, [currentMode]);

  const handleBinChange = (e) => {
    let value = e.target.value.trim().toLowerCase();
    setBinError(false);
    
    if (value && !value.includes('x') && value.length < 16) {
      if (value.startsWith('34') || value.startsWith('37')) {
        value = value.padEnd(15, 'x');
      } else {
        value = value.padEnd(16, 'x');
      }
    }
    setBin(value);
  };

  const handleGenerate = async () => {
    let currentBin = bin;
    if (currentBin === '') {
      currentBin = getRandomPopularBin();
      setBin(currentBin);
    }

    if (!validateBinInput(currentBin)) {
      setBinError(true);
      showToast('BIN inválido (solo números y "x")', 'error');
      return;
    } else {
      setBinError(false);
    }

    if (currentMode === 'ordered' && quantity > 1000) {
      showToast('En modo ordenado el máximo es 1000 tarjetas', 'warning');
      setQuantity(1000);
      return;
    }

    showLoading(true);
    
    // No usar await aquí, simplemente llamar a la función y dejar que se ejecute
    // La promesa se resuelve internamente y onGenerateComplete se llama cuando está lista
    processGeneration(currentBin, expMonth, expYear, quantity, includeLuhn, currentMode)
      .then(generated => {
        onGenerateComplete(generated);
        showToast(`${quantity} tarjetas generadas exitosamente`, 'success');
      })
      .catch(error => {
        console.error('Error durante la generación:', error);
        showToast('Error durante la generación', 'error');
      })
      .finally(() => {
        showLoading(false);
      });
  };

  const processGeneration = (binInput, expMonth, expYear, cardQuantity, includeLuhn, mode) => {
    return new Promise((resolve) => {
      // Eliminar el setTimeout para que la promesa se resuelva inmediatamente
      const isAmex = binInput.startsWith('34') || binInput.startsWith('37');
      const totalDigits = isAmex ? 15 : 16;
      const binBase = binInput.replace(/x/gi, '');
      const xCount = (binInput.match(/x/gi) || []).length;

      if (xCount === 0) {
        resolve('Error: El BIN debe contener al menos una "x" para generar variaciones.');
        return;
      }

      let result = '';
      const timestamp = new Date().toLocaleString();
      result += `> Generación iniciada: ${timestamp}\n`;
      result += `> BIN Base: ${binBase}\n`;
      result += `> Modo: ${mode}\n`;
      result += `> Cantidad: ${cardQuantity}\n`;
      result += `> Validación Luhn: ${includeLuhn ? 'Activada' : 'Desactivada'}\n`;
      result += `> ==========================================\n\n`;

      if (mode === 'ordered') {
        result += generateOrderedCards(binBase, xCount, totalDigits, expMonth, expYear, isAmex, cardQuantity, includeLuhn);
      } else {
        result += generateRandomCards(binBase, xCount, totalDigits, expMonth, expYear, isAmex, cardQuantity, includeLuhn);
      }

      result += `\n> ==========================================\n`;
      result += `> GENERACIÓN COMPLETADA\n`;
      result += `> ==========================================\n`;
      result += `> Total procesado: ${cardQuantity} tarjetas\n`;
      result += `> Status: ÉXITO\n`;
      result += `> Finalizado: ${new Date().toLocaleString()}\n`;
      result += `> ==========================================`;

      resolve(result);
    });
  };

  const generateOrderedCards = (binBase, xCount, totalDigits, expMonth, expYear, isAmex, cardQuantity, includeLuhn) => {
    const numberToGenerate = totalDigits - binBase.length - (includeLuhn ? 1 : 0);
    let result = '';

    const maxPossible = Math.pow(10, numberToGenerate);
    const finalQuantity = Math.min(cardQuantity, maxPossible, 1000);

    if (cardQuantity > finalQuantity) {
      result += `> ADVERTENCIA: Cantidad reducida a ${finalQuantity} para evitar overflow\n`;
      result += `> Máximo posible con este BIN: ${maxPossible.toLocaleString()}\n\n`;
    }

    for (let i = 0; i < finalQuantity; i++) {
      const paddedNumber = i.toString().padStart(numberToGenerate, '0');
      let cardNumberBase = binBase + paddedNumber;
      
      if (cardNumberBase.length > (totalDigits - (includeLuhn ? 1 : 0))) {
        break;
      }
      
      if (includeLuhn) {
        const checksum = calculateChecksum(cardNumberBase);
        cardNumberBase += checksum.toString();
      }

      const cvv = isAmex ? 
        Math.floor(Math.random() * 9000 + 1000).toString() : 
        Math.floor(Math.random() * 900 + 100).toString();

      result += `${cardNumberBase}|${expMonth}|${expYear}|${cvv}\n`;
    }

    return result;
  };

  const generateRandomCards = (binBase, xCount, totalDigits, expMonth, expYear, isAmex, cardQuantity, includeLuhn) => {
    const numberToGenerate = totalDigits - binBase.length - (includeLuhn ? 1 : 0);
    let result = '';

    for (let i = 0; i < cardQuantity; i++) {
      const randomNumber = Math.floor(Math.random() * Math.pow(10, numberToGenerate))
        .toString().padStart(numberToGenerate, '0');
      let cardNumberBase = binBase + randomNumber;
      
      if (includeLuhn) {
        const checksum = calculateChecksum(cardNumberBase);
        cardNumberBase += checksum.toString();
      }

      const cvv = isAmex ? 
        Math.floor(Math.random() * 9000 + 1000).toString() : 
        Math.floor(Math.random() * 900 + 100).toString();

      result += `${cardNumberBase}|${expMonth}|${expYear}|${cvv}\n`;
    }

    return result;
  };

  const years = Array.from({ length: 21 }, (_, i) => new Date().getFullYear() + i);
  const months = Array.from({ length: 12 }, (_, i) => (i + 1).toString().padStart(2, '0'));

  return (
    <div className="col-md-6">
      <h3 className={`mb-4 font-['Orbitron'] font-bold text-xl ${isDarkMode ? 'text-blue-400' : 'text-blue-600'}`}>
        <i className="fas fa-cogs mr-2"></i>Configuración
      </h3>
      
      <div className="mb-4">
        <label className={`form-label font-bold ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
          <i className="fas fa-credit-card mr-2"></i>BIN
        </label>
        <input 
          type="text" 
          className={`form-control-futuristic w-full px-4 py-3 mt-1 border rounded-lg shadow-inner focus:outline-none focus:ring-2 focus:ring-indigo-500 ${isDarkMode ? 'bg-gray-700/50 border-gray-600 text-white placeholder-gray-500' : 'bg-gray-50 border-gray-300 text-gray-900 placeholder-gray-400'} focus:border-indigo-500 transition-all duration-300`}
          id="bin" 
          placeholder="Ej: 543440xxxxxxxxxx"
          value={bin}
          onChange={handleBinChange}
          data-tooltip="Ingresa el BIN con 'x' para dígitos desconocidos"
        />
        {binError && (
          <div className="text-red-500 mt-2 text-sm">
            <i className="fas fa-exclamation-triangle mr-1"></i>
            BIN inválido (solo números y 'x')
          </div>
        )}
      </div>

      <div className="grid grid-cols-2 gap-4 mb-4">
        <div>
          <label className={`form-label font-bold ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
            <i className="fas fa-calendar-alt mr-2"></i>Mes
          </label>
          <select 
            className={`form-control-futuristic w-full px-4 py-3 mt-1 border rounded-lg shadow-inner focus:outline-none focus:ring-2 focus:ring-indigo-500 ${isDarkMode ? 'bg-gray-700/50 border-gray-600 text-white' : 'bg-gray-50 border-gray-300 text-gray-900'} focus:border-indigo-500 transition-all duration-300`}
            id="exp-month"
            value={expMonth}
            onChange={(e) => setExpMonth(e.target.value)}
          >
            {months.map(month => (
              <option key={month} value={month}>{month}</option>
            ))}
          </select>
        </div>
        <div>
          <label className={`form-label font-bold ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
            <i className="fas fa-calendar mr-2"></i>Año
          </label>
          <select 
            className={`form-control-futuristic w-full px-4 py-3 mt-1 border rounded-lg shadow-inner focus:outline-none focus:ring-2 focus:ring-indigo-500 ${isDarkMode ? 'bg-gray-700/50 border-gray-600 text-white' : 'bg-gray-50 border-gray-300 text-gray-900'} focus:border-indigo-500 transition-all duration-300`}
            id="exp-year"
            value={expYear}
            onChange={(e) => setExpYear(e.target.value)}
          >
            {years.map(year => (
              <option key={year} value={year}>{year}</option>
            ))}
          </select>
        </div>
      </div>

      <div className="mb-4">
        <label className={`form-label font-bold ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
          <i className="fas fa-hashtag mr-2"></i>Cantidad
        </label>
        <input 
          type="number" 
          className={`form-control-futuristic w-full px-4 py-3 mt-1 border rounded-lg shadow-inner focus:outline-none focus:ring-2 focus:ring-indigo-500 ${isDarkMode ? 'bg-gray-700/50 border-gray-600 text-white placeholder-gray-500' : 'bg-gray-50 border-gray-300 text-gray-900 placeholder-gray-400'} focus:border-indigo-500 transition-all duration-300`}
          id="card-quantity" 
          value={quantity} 
          onChange={(e) => setQuantity(parseInt(e.target.value))}
          min="1" 
          max={currentMode === 'ordered' ? "1000" : "10000"}
        />
      </div>

      <div className="mb-4">
        <div className="flex items-center">
          <input 
            type="checkbox" 
            className="form-check-input h-4 w-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500" 
            id="include-luhn" 
            checked={includeLuhn}
            onChange={(e) => setIncludeLuhn(e.target.checked)}
          />
          <label htmlFor="include-luhn" className={`ml-2 font-bold ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
            <i className="fas fa-shield-alt mr-2"></i>Validación Luhn
          </label>
        </div>
      </div>

      <div className="mb-4">
        <label className={`form-label font-bold ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
          <i className="fas fa-search mr-2"></i>Buscar
        </label>
        <input 
          type="text" 
          className={`form-control-futuristic w-full px-4 py-3 mt-1 border rounded-lg shadow-inner focus:outline-none focus:ring-2 focus:ring-indigo-500 ${isDarkMode ? 'bg-gray-700/50 border-gray-600 text-white placeholder-gray-500' : 'bg-gray-50 border-gray-300 text-gray-900 placeholder-gray-400'} focus:border-indigo-500 transition-all duration-300`}
          id="searchCards" 
          placeholder="Buscar en resultados..."
        />
      </div>

      <div className="text-center mt-8">
        <button 
          className="btn-futuristic bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white py-3 px-8 rounded-xl shadow-lg transition-all duration-300 transform hover:scale-105" 
          onClick={handleGenerate}
        >
          <i className="fas fa-play mr-2"></i>Generar
        </button>
      </div>
    </div>
  );
};

export default GeneratorForm;